/**
 * AppLayout Component
 *
 * Universal layout wrapper for all authenticated routes.
 * Uses shadcn SidebarProvider pattern for automatic layout management.
 *
 * Layout Structure:
 * ┌─────────────────────────────────────┐
 * │ AppHeader (sticky)                  │ ← Fixed header height
 * ├──────────┬──────────────────────────┤
 * │          │                          │
 * │ Sidebar  │   Main Content Area      │
 * │          │   (scrollable)           │
 * │          │                          │
 * │ 16rem or │   Auto-adjusted via      │
 * │ 3rem     │   peer selectors         │
 * │          │                          │
 * └──────────┴──────────────────────────┘
 *
 * Used by: /learner/*, /employer/*, /admin/* routes
 */

import { Outlet } from 'react-router-dom';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import { useScrollToTop } from '@/hooks/use-scroll-to-top';
import { AppSidebar } from './app-sidebar';
import { AppHeader } from './app-header';
import { AppBreadcrumb } from './app-breadcrumb';

/**
 * App layout with unified sidebar navigation
 *
 * Uses shadcn SidebarProvider pattern:
 * - Automatic collapse/expand state management
 * - Cookie persistence (sidebar:state)
 * - Responsive mobile Sheet on small screens
 * - Keyboard shortcut: Cmd/Ctrl + B
 *
 * Height/Scroll Strategy:
 * - Outer container is viewport height (h-screen)
 * - Header is fixed height at top
 * - Content area fills remaining space and scrolls independently
 * - SidebarInset's default min-h-svh is overridden to work within constrained height
 */
export const AppLayout = () => {
  // Scroll to top on route navigation (targets SidebarInset scroll container)
  useScrollToTop();

  return (
    <SidebarProvider defaultOpen={false}>
      <div className="flex h-screen w-full flex-col">
        {/* Header - fixed height sticky header */}
        <header className="sticky top-0 z-40 h-[var(--header-height)] w-full shrink-0 border-b bg-background">
          <AppHeader />
        </header>

        {/* Content area - fills remaining height */}
        <div className="flex min-h-0 flex-1">
          <AppSidebar />
          {/*
            SidebarInset renders as <main>, so we don't need another <main> inside.
            Override min-h-svh with min-h-0 to allow proper height calculation within
            the flex container, and h-full to fill available space.
          */}
          <SidebarInset className="flex h-full min-h-0 flex-col overflow-hidden">
            <AppBreadcrumb />
            <div className="min-h-0 flex-1 overflow-auto">
              {/* NO container here - pages decide their width */}
              <Outlet />
            </div>
          </SidebarInset>
        </div>
      </div>
    </SidebarProvider>
  );
};

